function fmtShort(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short" }).slice(0, 3).toUpperCase();
}

function fmtDay(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.getDate();
}

function diffDays(iso, todayIso) {
  return Math.round((new Date(iso) - new Date(todayIso)) / (1000 * 60 * 60 * 24));
}

export default function WeeklyStrip({ days, selected, onSelect, today, graceCutoff, dayTotals = {} }) {
  // days = [{iso, label}] for the Sun-Sat week currently being viewed.
  // `today` and `graceCutoff` are absolute ISO dates — the lock logic is
  // date-based so it crosses week boundaries correctly (e.g. on Sunday,
  // last Saturday is still editable because graceCutoff == yesterday).
  return (
    <div className="flex gap-2 overflow-x-auto" data-testid="weekly-strip">
      {days.map((d) => {
        const isToday = d.iso === today;
        const isFuture = diffDays(d.iso, today) > 0;
        const isPast = diffDays(d.iso, today) < 0;
        const editable = d.iso >= graceCutoff && !isFuture;
        const isSelected = selected === d.iso;
        const total = dayTotals[d.iso] ?? 0;

        let border = "border-[var(--stakes-border)]";
        let color = "var(--stakes-muted)";
        if (isSelected) {
          border = "border-white";
          color = "#fff";
        } else if (isToday) {
          border = "border-[var(--neon-blue)]";
          color = "var(--neon-blue)";
        } else if (editable) {
          color = "#fff";
        }

        return (
          <button
            key={d.iso}
            onClick={() => onSelect(d.iso)}
            disabled={isFuture}
            className={`flex-shrink-0 w-14 sm:w-16 border-2 ${border} p-2 flex flex-col items-center hover-flip ${
              isFuture ? "opacity-30 cursor-not-allowed" : ""
            } ${isSelected ? "bg-white text-black" : "bg-[var(--stakes-surface)]"}`}
            style={isSelected ? { color: "#000" } : { color }}
            data-testid={`weekly-strip-day-${d.iso}`}
          >
            <div className="label-mono" style={isSelected ? { color: "#000" } : { color }}>
              {fmtShort(d.iso)}
            </div>
            <div className="font-anton text-2xl leading-none">{fmtDay(d.iso)}</div>
            {isToday && !isSelected && (
              <div
                className="label-mono mt-0.5"
                style={{ color: "var(--neon-blue)", fontSize: "9px" }}
              >
                TODAY
              </div>
            )}
            {editable && !isToday && !isSelected && (
              <div
                className="label-mono mt-0.5"
                style={{ color: "var(--neon-blue)", fontSize: "9px" }}
                data-testid={`weekly-strip-grace-${d.iso}`}
              >
                GRACE
              </div>
            )}
            {!editable && isPast && (
              <div className="label-mono mt-0.5" style={{ fontSize: "9px" }} data-testid={`weekly-strip-lock-${d.iso}`}>
                LOCK
              </div>
            )}
            {total !== 0 && (
              <div
                className="label-mono mt-0.5"
                style={{
                  color: isSelected ? "#000" : total > 0 ? "var(--stakes-child)" : "var(--stakes-primary)",
                  fontSize: "10px",
                }}
              >
                {total > 0 ? "+" : ""}{total}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function buildWeekDays(today, weekOffset = 0) {
  // today is a YYYY-MM-DD string. Returns array of {iso} for Monday..Sunday
  // of the week shifted by `weekOffset` weeks (0 = current week containing
  // `today`; -1 = previous week; -2 = two weeks ago, etc.).
  // Phase 18 — Mon-Sun layout (was Sun-Sat) so the Fri-Sat-Sun Redemption
  // window lives inside one week.
  const d = new Date(today + "T00:00:00");
  const day = d.getDay(); // 0=Sun..6=Sat
  // Shift so Monday is the first day of the strip:
  //   Mon(1) → 0 days back  · Sun(0) → 6 days back
  const mondayOffsetDays = (day + 6) % 7;
  const start = new Date(d);
  start.setDate(d.getDate() - mondayOffsetDays + weekOffset * 7);
  const out = [];
  for (let i = 0; i < 7; i++) {
    const x = new Date(start);
    x.setDate(start.getDate() + i);
    const y = x.getFullYear();
    const m = String(x.getMonth() + 1).padStart(2, "0");
    const dd = String(x.getDate()).padStart(2, "0");
    out.push({ iso: `${y}-${m}-${dd}` });
  }
  return out;
}

/** Short human label for a viewed week relative to today. */
export function weekRangeLabel(days, today, weekOffset) {
  if (!days || days.length === 0) return "";
  if (weekOffset === 0) return "THIS WEEK (MON → SUN)";
  if (weekOffset === -1) return "LAST WEEK (MON → SUN)";
  const mon = days[0].iso;
  const sun = days[6].iso;
  const fmt = (iso) => {
    const dt = new Date(iso + "T00:00:00");
    return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" }).toUpperCase();
  };
  return `${fmt(mon)} → ${fmt(sun)}`;
}
